import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Select from 'flarum/common/components/Select';
import Button from 'flarum/common/components/Button';
import IndexSidebar from 'flarum/forum/components/IndexSidebar';
import PageStructure from 'flarum/forum/components/PageStructure';
import extractText from 'flarum/common/utils/extractText';
import VotingView from './VotingView';
import ResultsView from './ResultsView';
import MyVotesView from './MyVotesView';
export default class AwardsPage extends Page {
  constructor() {
    super(...arguments);
    _defineProperty(this, "loading", true);
    _defineProperty(this, "awards", []);
    _defineProperty(this, "selectedAward", null);
    _defineProperty(this, "selectedCategoryId", null);
    // null means "All Categories"
    _defineProperty(this, "currentView", 'categories');
    _defineProperty(this, "countdownInterval", null);
    _defineProperty(this, "countdownText", '');
    _defineProperty(this, "refreshInterval", null);
    _defineProperty(this, "routeAwardId", null);
    _defineProperty(this, "routeCategoryId", null);
  }
  oninit(vnode) {
    super.oninit(vnode);

    // Parse route params (format: /awards/{id}-{slug} or /awards/{id}-{slug}/{category})
    const idParam = m.route.param('id');
    const categoryParam = m.route.param('category');
    if (idParam) {
      // Extract numeric ID from "{id}-{slug}" format
      const match = idParam.match(/^(\d+)/);
      this.routeAwardId = match ? match[1] : null;
    }
    if (categoryParam) {
      this.routeCategoryId = categoryParam;
    }
    this.loadAwards();
  }
  oncreate(vnode) {
    super.oncreate(vnode);
    const navTitle = app.forum.attribute('awardsNavTitle') || 'Awards';
    app.setTitle(extractText(navTitle));
    this.startCountdown();
  }
  onremove(vnode) {
    super.onremove(vnode);
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
    this.stopAutoRefresh();
  }
  startCountdown() {
    this.updateCountdown();
    this.countdownInterval = window.setInterval(() => {
      this.updateCountdown();
      m.redraw();
    }, 1000);
  }
  updateCountdown() {
    if (!this.selectedAward) {
      this.countdownText = '';
      return;
    }
    const endsAt = this.selectedAward.endsAt();
    if (!endsAt) {
      this.countdownText = '';
      return;
    }
    const now = new Date();
    const diff = endsAt.getTime() - now.getTime();
    if (diff <= 0) {
      this.countdownText = app.translator.trans('huseyinfiliz-awards.forum.hero.voting_ended');
      return;
    }
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor(diff % (1000 * 60 * 60 * 24) / (1000 * 60 * 60));
    const minutes = Math.floor(diff % (1000 * 60 * 60) / (1000 * 60));
    const seconds = Math.floor(diff % (1000 * 60) / 1000);

    // Use single translation string with all placeholders
    this.countdownText = app.translator.trans('huseyinfiliz-awards.forum.hero.countdown', {
      days,
      hours,
      minutes,
      seconds
    });
  }
  async loadAwards() {
    this.loading = true;
    m.redraw();
    try {
      const awards = await app.store.find('awards', {
        include: 'categories,categories.nominees'
      });

      // Admins can see drafts (for preview), others only see active/ended/published
      const canManage = app.forum.attribute('canManageAwards');
      this.awards = (awards || []).filter(a => {
        if (canManage) return true; // Admins see all
        return a.isActive() || a.isPublished() || a.hasEnded();
      });

      // If route has award ID, select that award; otherwise use default selection
      if (this.routeAwardId) {
        this.selectedAward = this.awards.find(a => String(a.id()) === this.routeAwardId) || null;
      }

      // Fallback to default selection if route award not found
      if (!this.selectedAward) {
        this.selectedAward = this.awards.find(a => a.isActive()) || this.awards.find(a => a.isPublished()) || this.awards[0] || null;
      }

      // Set default view based on award status
      if (this.selectedAward?.canViewResults()) {
        this.currentView = 'results';
      } else {
        this.currentView = 'categories';
      }

      // If route has category ID, set the category filter
      if (this.routeCategoryId && this.selectedAward) {
        const categories = this.selectedAward.categories?.() || [];
        const categoryExists = categories.some(c => String(c.id()) === this.routeCategoryId);
        if (categoryExists) {
          this.selectedCategoryId = this.routeCategoryId;
        }
      }
      if (app.session.user) {
        await this.loadUserVotes();
      }
    } catch (error) {
      console.error('Failed to load awards:', error);
    }
    this.loading = false;
    this.updateCountdown();
    this.startAutoRefresh();

    // Update URL to reflect current selection (normalizes URL format)
    this.updateUrl();
    m.redraw();

    // Scroll to category after DOM updates if category was specified in route
    if (this.routeCategoryId && this.selectedCategoryId) {
      setTimeout(() => this.scrollToCategory(this.routeCategoryId), 100);
    }
  }
  startAutoRefresh() {
    if (!this.selectedAward) return;

    // Only refresh if voting is open and live votes are shown
    if (this.selectedAward.isVotingOpen() && this.selectedAward.showLiveVotes()) {
      this.stopAutoRefresh(); // Clear any existing interval
      this.refreshInterval = window.setInterval(() => {
        this.refreshVoteCounts();
      }, 60000); // 60 seconds
    }
  }
  stopAutoRefresh() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
    }
  }
  async refreshVoteCounts() {
    if (!this.selectedAward) return;
    try {
      await app.store.find('awards', this.selectedAward.id(), {
        include: 'categories,categories.nominees'
      });
      m.redraw();
    } catch (error) {
      console.error('Failed to refresh vote counts:', error);
    }
  }
  async loadUserVotes() {
    try {
      await app.store.find('award-votes', {
        page: {
          limit: 250
        }
      });
    } catch (error) {
      console.error('Failed to load user votes:', error);
    }
  }
  view() {
    return m(PageStructure, {
      className: "IndexPage huseyinfiliz-awards",
      hero: this.hero.bind(this),
      sidebar: this.sidebar.bind(this)
    }, this.loading ? m(LoadingIndicator, null) : this.content());
  }
  sidebar() {
    return m(IndexSidebar, null);
  }
  hero() {
    const navTitle = app.forum.attribute('awardsNavTitle') || 'Awards';
    const navIcon = app.forum.attribute('awardsNavIcon') || 'fas fa-trophy';
    const imageUrl = this.selectedAward?.imageUrl?.();
    const hasImage = imageUrl && imageUrl.length > 0;
    return m("header", {
      className: `Hero AwardsHero ${hasImage ? 'AwardsHero--withImage' : ''}`,
      style: hasImage ? {
        backgroundImage: `url(${imageUrl})`
      } : undefined
    }, hasImage ? m("div", {
      className: "AwardsHero-overlay"
    }) : null, m("div", {
      className: "container"
    }, m("div", {
      className: "containerNarrow"
    }, m("h1", {
      className: "Hero-title"
    }, m("i", {
      className: `icon ${navIcon}`
    }), " ", navTitle), this.selectedAward ? m("div", {
      className: "Hero-subtitle"
    }, this.selectedAward.description()) : null, this.selectedAward && this.selectedAward.isVotingOpen() && this.countdownText ? m("div", {
      className: "AwardsHero-countdown"
    }, m("i", {
      className: "fas fa-clock"
    }), " ", this.countdownText) : null, this.selectedAward && !this.selectedAward.isVotingOpen() && this.selectedAward.hasEnded() && !this.selectedAward.isPublished() ? m("div", {
      className: "AwardsHero-countdown AwardsHero-countdown--ended"
    }, app.translator.trans('huseyinfiliz-awards.forum.hero.voting_ended')) : null, this.selectedAward && this.selectedAward.isPublished() ? m("div", {
      className: "AwardsHero-countdown AwardsHero-countdown--published"
    }, app.translator.trans('huseyinfiliz-awards.forum.hero.results_published')) : null, this.selectedAward && this.selectedAward.isDraft() ? m("div", {
      className: "AwardsHero-countdown AwardsHero-countdown--draft"
    }, m("i", {
      className: "fas fa-eye"
    }), " ", app.translator.trans('huseyinfiliz-awards.forum.hero.draft_preview')) : null, this.selectedAward && this.selectedAward.hasEnded() && !this.selectedAward.isPublished() && this.selectedAward.canViewResults() ? m("div", {
      className: "AwardsHero-countdown AwardsHero-countdown--preview"
    }, m("i", {
      className: "fas fa-eye"
    }), " ", app.translator.trans('huseyinfiliz-awards.forum.hero.results_preview')) : null)));
  }
  content() {
    const navIcon = app.forum.attribute('awardsNavIcon') || 'fas fa-trophy';
    if (this.awards.length === 0) {
      return m("div", {
        className: "EmptyState"
      }, m("i", {
        className: navIcon
      }), m("h2", null, app.translator.trans('huseyinfiliz-awards.forum.empty.no_awards')), m("p", null, app.translator.trans('huseyinfiliz-awards.forum.empty.no_awards_description')));
    }
    return m("div", {
      className: "AwardsPage-content"
    }, this.renderFilterBar(), this.renderAwardView());
  }
  renderFilterBar() {
    const categories = this.selectedAward?.categories?.() || [];
    const showResultsTab = this.selectedAward?.isPublished();
    const showMyVotesTab = app.session.user;

    // Build category filter options with 'cat_' prefix to prevent numeric key sorting
    // (JS objects sort integer-index keys first, so "1", "2" would come before "all")
    const categoryOptions = {
      all: app.translator.trans('huseyinfiliz-awards.forum.nav.all_categories')
    };
    categories.forEach(cat => {
      categoryOptions[`cat_${cat.id()}`] = cat.name();
    });
    return m("div", {
      className: "AwardsPage-filterBar"
    }, m("div", {
      className: "AwardsPage-filterBar-left"
    }, this.awards.length > 1 ? this.renderAwardSelector() : null, categories.length > 0 && this.currentView !== 'my_votes' ? m("div", {
      className: "AwardsPage-categoryFilter"
    }, m(Select, {
      value: this.selectedCategoryId ? `cat_${this.selectedCategoryId}` : 'all',
      options: categoryOptions,
      onchange: value => {
        this.selectedCategoryId = value === 'all' ? null : value.replace('cat_', '');
        this.updateUrl();
        m.redraw();
        if (this.selectedCategoryId) {
          setTimeout(() => this.scrollToCategory(this.selectedCategoryId), 100);
        }
      }
    })) : null), m("div", {
      className: "AwardsPage-filterBar-right"
    }, m("div", {
      className: "AwardsPage-tabs"
    }, m(Button, {
      className: `Button ${this.currentView === (showResultsTab ? 'results' : 'categories') ? 'Button--primary' : ''}`,
      onclick: () => {
        this.currentView = showResultsTab ? 'results' : 'categories';
        m.redraw();
      }
    }, showResultsTab ? app.translator.trans('huseyinfiliz-awards.forum.tabs.results') : app.translator.trans('huseyinfiliz-awards.forum.tabs.categories')), showMyVotesTab ? m(Button, {
      className: `Button ${this.currentView === 'my_votes' ? 'Button--primary' : ''}`,
      onclick: () => {
        this.currentView = 'my_votes';
        m.redraw();
      },
      icon: "fas fa-clipboard-list"
    }, app.translator.trans('huseyinfiliz-awards.forum.tabs.my_votes')) : null)));
  }
  renderAwardSelector() {
    // Use 'award_' prefix to prevent numeric key sorting in JS objects
    const awardOptions = {};
    this.awards.forEach(award => {
      awardOptions[`award_${award.id()}`] = `${award.name()} (${award.year()})`;
    });
    return m("div", {
      className: "AwardsPage-selector"
    }, m(Select, {
      value: `award_${this.selectedAward?.id()}`,
      options: awardOptions,
      onchange: value => {
        const awardId = value.replace('award_', '');
        this.selectedAward = this.awards.find(a => String(a.id()) === awardId) || null;
        this.selectedCategoryId = null;
        this.updateCountdown();
        // Update view based on new award status
        if (this.selectedAward?.canViewResults()) {
          this.currentView = 'results';
        } else {
          this.currentView = 'categories';
        }
        this.updateUrl();
        m.redraw();
      }
    }));
  }
  scrollToCategory(categoryId) {
    if (!categoryId) return;
    const element = document.getElementById(`category-${categoryId}`);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  }
  updateUrl() {
    if (!this.selectedAward) return;
    const awardId = this.selectedAward.id();
    const awardSlug = this.selectedAward.slug() || 'award';
    let path;
    if (this.selectedCategoryId) {
      path = app.route('awards.category', {
        id: `${awardId}-${awardSlug}`,
        category: this.selectedCategoryId
      });
    } else {
      path = app.route('awards.show', {
        id: `${awardId}-${awardSlug}`
      });
    }

    // Use history.replaceState to update URL without navigation
    history.replaceState(null, '', path);
  }
  renderAwardView() {
    const award = this.selectedAward;

    // My Votes view
    if (this.currentView === 'my_votes' && app.session.user) {
      return m(MyVotesView, {
        award: award,
        onNavigateToCategory: categoryId => {
          this.currentView = 'categories';
          this.selectedCategoryId = categoryId;
          this.updateUrl();
          m.redraw();
          // Delay scroll to allow DOM update
          setTimeout(() => this.scrollToCategory(categoryId), 100);
        }
      });
    }

    // Results view
    if (award.canViewResults()) {
      return m(ResultsView, {
        award: award,
        selectedCategoryId: this.selectedCategoryId
      });
    }

    // Voting view (including draft preview for admins)
    if (award.isActive() || award.hasEnded() || award.isDraft()) {
      return m("div", null, award.isDraft() ? m("div", {
        className: "Alert Alert--warning AwardsPage-draftAlert"
      }, m("i", {
        className: "fas fa-eye"
      }), " ", app.translator.trans('huseyinfiliz-awards.forum.preview.admin_only')) : null, m(VotingView, {
        award: award,
        selectedCategoryId: this.selectedCategoryId,
        onCategoryChange: categoryId => {
          this.selectedCategoryId = categoryId;
          this.updateUrl();
          m.redraw();
        }
      }));
    }
    return m("div", {
      className: "EmptyState"
    }, m("p", null, app.translator.trans('huseyinfiliz-awards.forum.error.not_available')));
  }
}
flarum.reg.add('huseyinfiliz-awards', 'forum/components/AwardsPage', AwardsPage);